export type AssistantIntentCategory =
  | 'site'
  | 'content'
  | 'customers'
  | 'analytics'
  | 'settings'
  | 'publish';

export type AssistantIntentParam = {
  /** Clave lógica del dato que se ejecuta (ej. 'hours', 'whatsappMessage'). */
  key: string;
  /** Pregunta que el composer hace para capturar el valor. */
  prompt: string;
  placeholder?: string;
};

export type AssistantIntentExecution = {
  /**
   * 'route'   → solo lleva al lugar correcto (sin mutar datos).
   * 'execute' → ejecuta una mutación real en el servidor (POST /assistant/execute,
   *             con confirmación según requiresConfirmation y captura de parámetro
   *             si hay `param`). La bitácora se escribe en la misma petición.
   */
  mode: 'route' | 'execute';
  param?: AssistantIntentParam;
};

export type AssistantIntent = {
  id: string;
  label: string;
  description: string;
  category: AssistantIntentCategory;
  platforms: Array<'mobile' | 'desktop'>;
  risk: 'low' | 'medium' | 'high';
  requiresConfirmation: boolean;
  requiresContext?: string[];
  destination?: {
    kind: 'route' | 'modal' | 'sheet' | 'inline-action';
    value: string;
  };
  handler?: {
    kind: 'navigate' | 'prefill' | 'api-call' | 'multi-step';
    value: string;
  };
  execution: AssistantIntentExecution;
  successMessage?: string;
  failureMessage?: string;
};

export const assistantIntents: AssistantIntent[] = [
  {
    id: 'change-business-hours',
    label: 'Cambiar horario',
    description: 'Actualiza el horario de atención que se muestra en tu sitio.',
    category: 'site',
    platforms: ['mobile', 'desktop'],
    risk: 'low',
    requiresConfirmation: false,
    destination: { kind: 'route', value: '/site?tab=contacto' },
    handler: { kind: 'api-call', value: 'site.contact.hours' },
    execution: {
      mode: 'execute',
      param: { key: 'hours', prompt: '¿Cuál es el nuevo horario?', placeholder: 'Lun–Vie 9:00–18:00' },
    },
    successMessage: 'Listo, actualicé tu horario.',
    failureMessage: 'No pude actualizar el horario. Inténtalo de nuevo.',
  },
  {
    id: 'publish-announcement',
    label: 'Publicar aviso',
    description: 'Crea un aviso nuevo para mostrarlo en tu sitio.',
    category: 'content',
    platforms: ['mobile', 'desktop'],
    risk: 'medium',
    requiresConfirmation: false,
    destination: { kind: 'route', value: '/site?tab=anuncios' },
    handler: { kind: 'api-call', value: 'site.announcements.create' },
    execution: {
      mode: 'execute',
      param: { key: 'message', prompt: '¿Qué quieres anunciar?', placeholder: 'Ej. Cerramos el lunes por feriado' },
    },
    successMessage: 'Creé tu aviso.',
    failureMessage: 'No pude crear el aviso. Inténtalo de nuevo.',
  },
  {
    id: 'edit-homepage-copy',
    label: 'Editar texto del inicio',
    description: 'Te lleva al editor de la portada para ajustar los textos.',
    category: 'content',
    platforms: ['mobile', 'desktop'],
    risk: 'low',
    requiresConfirmation: false,
    destination: { kind: 'route', value: '/site?tab=paginas' },
    handler: { kind: 'navigate', value: '/site?tab=paginas' },
    execution: { mode: 'route' },
  },
  {
    id: 'check-new-leads',
    label: 'Revisar consultas',
    description: 'Muestra las consultas pendientes para responder rápido.',
    category: 'customers',
    platforms: ['mobile', 'desktop'],
    risk: 'low',
    requiresConfirmation: false,
    destination: { kind: 'route', value: '/submissions' },
    handler: { kind: 'navigate', value: '/submissions' },
    execution: { mode: 'route' },
  },
  {
    id: 'mark-leads-read',
    label: 'Marcar consultas como leídas',
    description: 'Pone al día tus consultas marcándolas todas como leídas.',
    category: 'customers',
    platforms: ['mobile', 'desktop'],
    risk: 'low',
    requiresConfirmation: false,
    handler: { kind: 'api-call', value: 'submissions.markRead' },
    execution: { mode: 'execute' },
    successMessage: 'Marqué tus consultas como leídas.',
    failureMessage: 'No pude marcarlas como leídas. Inténtalo de nuevo.',
  },
  {
    id: 'publish-site',
    label: 'Publicar sitio',
    description: 'Lanza la publicación para que tus cambios se vean en tu sitio en vivo.',
    category: 'publish',
    platforms: ['mobile', 'desktop'],
    risk: 'medium',
    requiresConfirmation: true,
    destination: { kind: 'route', value: '/site?tab=paginas' },
    handler: { kind: 'multi-step', value: 'site.publish' },
    execution: { mode: 'execute' },
    successMessage: 'Empecé a publicar tu sitio. En unos minutos estará en vivo.',
    failureMessage: 'No se pudo iniciar la publicación. Revisa el estado y vuelve a intentarlo.',
  },
  {
    id: 'update-whatsapp-message',
    label: 'Cambiar mensaje de WhatsApp',
    description: 'Ajusta el mensaje con el que el visitante te escribe por WhatsApp.',
    category: 'site',
    platforms: ['mobile', 'desktop'],
    risk: 'low',
    requiresConfirmation: false,
    destination: { kind: 'route', value: '/site?tab=contacto' },
    handler: { kind: 'api-call', value: 'site.contact.whatsappMessage' },
    execution: {
      mode: 'execute',
      param: { key: 'whatsappMessage', prompt: '¿Cuál es el nuevo mensaje de WhatsApp?', placeholder: 'Hola, quiero más información' },
    },
    successMessage: 'Actualicé tu mensaje de WhatsApp.',
    failureMessage: 'No pude actualizar el mensaje. Inténtalo de nuevo.',
  },
  {
    id: 'create-content-section',
    label: 'Crear sección de contenido',
    description: 'Te lleva a agregar una nueva sección de contenido a tu sitio.',
    category: 'content',
    platforms: ['mobile', 'desktop'],
    risk: 'low',
    requiresConfirmation: false,
    destination: { kind: 'route', value: '/site?tab=paginas' },
    handler: { kind: 'navigate', value: '/site?tab=paginas' },
    execution: { mode: 'route' },
  },
];
